<template>
  <nav class="accountants-submenu" :class="{ collapsed: menuCollapsed }">
    <slot name="titletop" />
    <ul class="accountants-menu--menuitems" role="menu">
      <li
        v-for="item in visibleItems"
        :key="item.key || item.name"
        class="menuitem"
        role="none"
        :class="itemClass(item)"
        v-b-tooltip.hover.right="{
          title: item.label,
          disabled: !menuCollapsed
        }"
      >
        <router-link
          role="menuitem"
          :to="resolveTo(item)"
          :data-analytics-click="'submenu ' + item.analytics"
          class="btn btn-light-square"
        >
          <div class="menuitem--icon" v-if="item.icon">
            <font-awesome-icon :icon="[isActive(item) ? 'fa-solid' : 'fa-regular', item.icon]" />
          </div>
          <span class="menuitem-label">{{ item.label }}</span>
        </router-link>
      </li>
    </ul>
    <slot name="bottom" />

    <div class="accountants-menu--toggle" @click.prevent="toggleMenu">
      <div class="menuitem">
        <div class="menuitem--icon">
          <font-awesome-icon icon="fa-solid fa-chevron-left" />
        </div>
      </div>
    </div>

  </nav>
</template>


<script>
import { mapGetters, mapMutations } from 'vuex'

export default {
  name: 'AccountantsSubmenu',
  components: {

  },
  props: {
    /**
     * Array of items:
     * {
     *   key?: string,                 // for v-for
     *   label: string,                // text label
     *   name?: string,                // vue-router named route
     *   to?: Object | String,         // optional full `to` if you don't use name
     *   params?: Object,              // optional params (merged with routeParams)
     *   icon?: String | Array,        // font-awesome icon
     *   analytics?: String,           // data-analytics-click value
     *   show?: Boolean|Function       // condition or (ctx) => boolean
     * }
     */
    items: { type: Array, required: true },

    /**
     * Common route params merged into each item (e.g. { customerId })
     */
    routeParams: { type: Object, default: () => ({}) },

    /**
     * Class name to apply on active <li>
     */
    activeClassName: { type: String, default: 'selected-tab' },
  },
  data() {
    return {
    }
  },
  computed: {
    ...mapGetters({
      menuCollapsed: 'accountants/getCollapsedMenu'
    }),
    visibleItems() {
      return this.items.filter(item => {
        return item.show === undefined ? true : !!item.show;
      });
    },
  },  
  methods: {
    ...mapMutations({
      toggleMenu: 'accountants/TOGGLE_MENU',
    }),
    resolveTo(item) {
      if (item.to) return item.to;
      if (item.name) {
        // merge per-item params with global ones; item.params wins
        const mergedParams = Object.assign({}, this.routeParams, item.params || {});
        return { name: item.name, params: mergedParams };
      }
      // Fallback so router-link doesn't break
      return { path: this.$route.path };
    },
    isActive(item) {
      let is_active = false
      // 1) route name matches item.name
      if (item.name && this.$route.name === item.name) {
        is_active = true
      }
      // 2) route path contains item.key
      else if (item.key && this.$route.path.includes('/' + item.key + '/')) {
        is_active = true
      }
      return is_active
    },
    itemClass(item) {
      return { [this.activeClassName]: this.isActive(item) };
    },
  },
}
</script>

<style lang="scss">
.accountants-submenu {
  display: flex;
  flex-direction: column;
  // overflow: hidden;
  z-index: 111;
  width: 235px;
  max-width: 235px;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  padding: 0 0.75rem;
  padding-top: 1rem;
  padding-right: 0;
  transition: all 0.2s;
  position: relative;
  // border-right: 1px solid $colorBorderDash;
  .submenu-title {
    padding: 0.5rem 0;
    font-family: $fontMedium;
    color: $colorTextSemi;
    font-size: 18px;
    margin-bottom: 0.5rem;
    white-space: nowrap;
    transition: opacity 0.1s;
  }
  &.collapsed {
    padding-left: 0.5rem;
    padding-right: 0;
    max-width: var(--accountants-submenu-size);
    .submenu-title {
      opacity: 0;
    }
    ul.accountants-menu--menuitems {
      li.menuitem {
        a {
          padding-left: 0.5rem;
          .menuitem-label {
            opacity: 0;
          }
        }
        .svg-inline--fa { margin: 0; }
        &.selected-tab > a,
        a:hover {
          .menuitem--icon {
            svg {
              transform: scale(1.15);
            }
          }
        }
      }
    }
    .accountants-menu--toggle {
      transform: rotate(180deg);
      margin-left: 6px;
    }
  }
  ul.accountants-menu--menuitems {
    display: flex;
    flex-direction: column;    
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 0.25rem;

    li.menuitem {
      display: flex;
      align-items: center;
      a {
        display: flex;
        align-items: center;
        text-decoration: none;
        padding: 0.5rem 0.75rem;
        color: $colorTextBase;
        font-size: 1rem;
        width: 100%;
        min-height: 30px;
        gap: 0.25rem;
        position: relative;
        border: 0;
        .menuitem--icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          flex-shrink: 0;
          transition: all .1s linear;
          z-index: 1;
        }
        .menuitem-label {
          transition: opacity 0.1s;
          white-space: nowrap;
          z-index: 1;
        }
        &:before {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
          border: 1px solid $colorBorderDash;
          border-right: 0;
        }
      }
      &.selected-tab {
        a {
          &:before {
            transform: scale(1);
            background: white;
            opacity: 1;
          }
        }
      }
    }
  }
  .accountants-menu--toggle {
    // position: absolute;
    // top: 1rem;
    // right: -13px;
    margin-top: 1rem;
    width: 26px;
    // transform: translateY(-50%);
    background: #fff;
    border-radius: 50em;
    color: $colorBrand;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
    outline: 1px solid $colorBorderDash;
    font-size: 12px;
    display: none;
    margin-left: auto;
    margin-right: 0.5rem;
    cursor: pointer;
  }
  &.collapsed,
  &:hover {
    .accountants-menu--toggle {
      display: flex;
    }
  }
}

</style>
